import React, { useRef, useState } from 'react';
import { has } from 'lodash';
import Bubble from '@components/Bubble';
import { Eye, Remove } from '@icons/crm';
import styled from 'styled-components';

import Notification from '../Notification';
import InfiniteList from '../../../InfiniteList';
import Loader from '@components/Loader';
import {
  DeleteNotificationsPayload,
  GoToNotificationPayload,
  ReadNotificationsPayload,
} from '../../../../store/actions/notifications';
import { INotification, INotificationsListMeta } from 'types/types';

interface INotificationsProps {
  className?: string;
  isLoading: boolean;
  notifications: INotification[];
  notificationsMeta: INotificationsListMeta;
  setNotificationsPage: (page: number) => void;
  readNotifications: (params: ReadNotificationsPayload) => void;
  deleteNotifications: (params: DeleteNotificationsPayload) => void;
  goToNotificationEntity: (params: GoToNotificationPayload) => void;
  setIsOpen: (isOpen: boolean) => void;
}

const Notifications = ({
  className,
  isLoading,
  notifications,
  notificationsMeta,
  setNotificationsPage,
  readNotifications,
  deleteNotifications,
  goToNotificationEntity,
  setIsOpen,
}: INotificationsProps) => {
  const [isUpdatingList, setIsUpdatingList] = useState(false);

  const listRef = useRef(null);

  const { page, totalPages, unreadCount } = notificationsMeta;

  const hasNextPage = page < totalPages;

  const isItemLoaded = index => has(notifications, `${index}.id`);

  const getItemKey = (index, logs) => {
    if (!logs[index]) {
      return `empty-${index}`;
    }
    return logs[index].id;
  };

  const handleLoadPage = () => {
    setNotificationsPage(page + 1);
  };

  const handleReadNotifications = (params: ReadNotificationsPayload) => {
    const { markAll, id, onSuccess } = params;

    if (markAll) {
      readNotifications({ markAll, onSuccess });
      return;
    }

    readNotifications({ id, onSuccess });
  };

  const handleDeleteNotifications = (params: DeleteNotificationsPayload) => {
    const { deleteAll, id, onSuccess } = params;

    if (deleteAll) {
      deleteNotifications({ deleteAll, onSuccess });
      return;
    }

    deleteNotifications({ id, onSuccess });
  };

  const handleReadAllNotifications = () => {
    setIsUpdatingList(true);

    handleReadNotifications({
      markAll: true,
      onSuccess: () => setIsUpdatingList(false),
    });
  };

  const handleDeleteAllNotifications = () => {
    setIsUpdatingList(true);

    handleDeleteNotifications({
      deleteAll: true,
      onSuccess: () => setIsUpdatingList(false),
    });
  };

  return (
    <StyledWrapper className={className}>
      {isUpdatingList && <StyledUpdatingLoader />}
      <StyledHeader>
        <StyledTitle>Уведомления</StyledTitle>
        <StyledButtons>
          {!!unreadCount && (
            <StyledBubble placement="top" text="Пометить все как прочитанные">
              <StyledButton onClick={handleReadAllNotifications}>
                <Eye width={18} />
              </StyledButton>
            </StyledBubble>
          )}
          {!!notifications.length && (
            <StyledBubble placement="top" text="Удалить все">
              <StyledButton onClick={handleDeleteAllNotifications}>
                <Remove width={18} />
              </StyledButton>
            </StyledBubble>
          )}
        </StyledButtons>
      </StyledHeader>
      <StyledList>
        {isLoading ? (
          <StyledLoader size={50} />
        ) : (
          <>
            {hasNextPage || notifications.length ? (
              <InfiniteList
                itemSize={90}
                isItemLoaded={isItemLoaded}
                itemCount={
                  hasNextPage ? notifications.length + 1 : notifications.length
                }
                itemData={notifications}
                itemKey={getItemKey}
                onLoadRequest={handleLoadPage}
                outerRef={listRef}
                threshold={2}
              >
                {({ data, index, style }) => {
                  if (!isItemLoaded(index)) {
                    return null;
                  }

                  const notification = data[index];

                  return (
                    <StyledNotificationItem
                      key={`notification_${notification.id}`}
                      data-id={notification.id}
                      style={style}
                    >
                      <Notification
                        notification={notification}
                        onReadNotifications={handleReadNotifications}
                        onDeleteNotifications={handleDeleteNotifications}
                        goToNotificationEntity={goToNotificationEntity}
                        setIsOpenDropdown={setIsOpen}
                      />
                    </StyledNotificationItem>
                  );
                }}
              </InfiniteList>
            ) : (
              <StyledEmptyList>Уведомлений нет</StyledEmptyList>
            )}
          </>
        )}
      </StyledList>
    </StyledWrapper>
  );
};

const StyledLoader = styled(Loader)`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
`;

const StyledEmptyList = styled.div`
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  font-size: 24px;
`;

const StyledNotificationItem = styled.li`
  position: relative;
  color: #4d4d4d;

  /* &:not(:last-child) {
    margin-bottom: 8px;

    &::before {
      content: '';
      width: 2px;
      height: calc(100% + 12px);
      display: block;
      position: absolute;
      background: #e0e0e0;
      left: 14px;
    }
  } */
`;

const StyledList = styled.ul`
  width: 100%;
  height: 400px;
`;

const StyledButton = styled.button`
  display: flex;
  border: none;
  outline: none;
  background: transparent;
  cursor: pointer;
  border-radius: 2px;
`;

const StyledBubble = styled(Bubble)`
  & > div {
    display: flex;
  }
`;

const StyledButtons = styled.div`
  display: flex;
  gap: 10px;
`;

const StyledTitle = styled.div`
  font-size: 14px;
  font-weight: bold;
`;

const StyledHeader = styled.div`
  border-bottom: 1px solid #c2c5cc;
  display: flex;
  align-items: center;
  padding: 8px;
  justify-content: space-between;
`;

const StyledUpdatingLoader = styled(StyledLoader)`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: rgb(255 255 255 / 75%);
  z-index: 99;
`;

const StyledWrapper = styled.div`
  position: relative;
  height: 100%;
`;

export default Notifications;
